import { CheckCircle, ClipboardList, Clock, Lock } from "@tamagui/lucide-icons";
import type { ImageSourcePropType } from "react-native";

import DoctorOutline from "@/assets/icons/DoctorOutline";
import WeightScaleOutline from "@/assets/icons/WeightScaleOutline";
import Headphones from "@/assets/images/landing_page/headphones.png";
import Hormone from "@/assets/images/landing_page/hormone.png";
import Pills from "@/assets/images/landing_page/pills.png";
import Salad from "@/assets/images/landing_page/salad.png";
import GenericCarousel1 from "@/assets/images/landing_page/v3/generic-carousel-01.png";
import GenericCarousel2 from "@/assets/images/landing_page/v3/generic-carousel-02.png";
import GenericCarousel3 from "@/assets/images/landing_page/v3/generic-carousel-03.png";
import GenericCarousel4 from "@/assets/images/landing_page/v3/generic-carousel-04.png";
import GenericTestimonial from "@/assets/images/landing_page/v3/generic-testimonial-01.png";
import ImproveMenopauseAccordion1 from "@/assets/images/landing_page/v3/improve-menopause-accordion-01.png";
import ImproveMenopauseAccordion2 from "@/assets/images/landing_page/v3/improve-menopause-accordion-02.png";
import ImproveMenopauseAccordion3 from "@/assets/images/landing_page/v3/improve-menopause-accordion-03.png";
import ImproveWeightAccordion1 from "@/assets/images/landing_page/v3/improve-weight-accordion-01.png";
import ImproveWeightAccordion2 from "@/assets/images/landing_page/v3/improve-weight-accordion-02.png";
import ImproveWeightAccordion3 from "@/assets/images/landing_page/v3/improve-weight-accordion-03.png";
import ImproveWeightCarousel1 from "@/assets/images/landing_page/v3/improve-weight-carousel-01.png";
import ImproveWeightCarousel2 from "@/assets/images/landing_page/v3/improve-weight-carousel-02.png";
import ImproveWeightCarousel3 from "@/assets/images/landing_page/v3/improve-weight-carousel-03.png";
import ImproveWeightCarousel4 from "@/assets/images/landing_page/v3/improve-weight-carousel-04.png";
import MedicalMenopauseAccordion1 from "@/assets/images/landing_page/v3/medical-menopause-accordion-01.png";
import MedicalMenopauseAccordion2 from "@/assets/images/landing_page/v3/medical-menopause-accordion-02.png";
import MedicalMenopauseAccordion3 from "@/assets/images/landing_page/v3/medical-menopause-accordion-03.png";
import MedicalWeightAccordion1 from "@/assets/images/landing_page/v3/medical-weight-accordion-01.png";
import MedicalWeightAccordion2 from "@/assets/images/landing_page/v3/medical-weight-accordion-02.png";
import MedicalWeightAccordion3 from "@/assets/images/landing_page/v3/medical-weight-accordion-03.png";
import MedicalWeightCarousel1 from "@/assets/images/landing_page/v3/medical-weight-carousel-01.png";
import MedicalWeightCarousel2 from "@/assets/images/landing_page/v3/medical-weight-carousel-02.png";
import MedicalWeightCarousel3 from "@/assets/images/landing_page/v3/medical-weight-carousel-03.png";
import MedicalWeightCarousel4 from "@/assets/images/landing_page/v3/medical-weight-carousel-04.png";
import MenopauseCarousel1 from "@/assets/images/landing_page/v3/menopause-carousel-01.png";
import MenopauseCarousel2 from "@/assets/images/landing_page/v3/menopause-carousel-02.png";
import MenopauseCarousel3 from "@/assets/images/landing_page/v3/menopause-carousel-03.png";
import MenopauseCarousel4 from "@/assets/images/landing_page/v3/menopause-carousel-04.png";
import MenopauseUserTestimonial from "@/assets/images/landing_page/v3/menopause-user-testimonial-01.png";
import WeightUserTestimonial from "@/assets/images/landing_page/v3/weight-user-testimonial-01.png";
import Walking from "@/assets/images/landing_page/walking.png";
import WomanSleeping from "@/assets/images/landing_page/woman_sleeping.png";
import type { AccordionDrawerProps } from "@/components/Accordion";
import type { TyperControllerProps } from "@/components/animated/Typer";
import { brandName, shortBrandName } from "@/config/brand";
import type { AtAGlanceProps } from "@/landing-page/v3/AtAGlance";
import { FAQItem } from "@/landing-page/v3/FAQ";
import type { TestimonialProps } from "@/landing-page/v3/Testimonial";
import { Agent } from "@/types/agent";
import { isMobileWeb } from "@/utils/web";

/**
 * This hook was previously powered by GET /landing/suggestions. To minimize time until the
 * FCP (First Contentful Paint) of the landing page, this is currently hardcoded on the frontend.
 */

type SuggestionText =
  | "manage medications"
  | "address low libido"
  | "clear brain fog"
  | "mental health support"
  | "check symptoms"
  | "improve sleep"
  | "ease night sweats"
  | "lose weight"
  | "improve gut health";

export interface Suggestion {
  text: SuggestionText;
  agent?: Agent;
}

interface LandingSuggestions {
  headings: string[];
  suggestions: Suggestion[];
}

export const useGetLandingSuggestions = (): LandingSuggestions => {
  const headings: string[] = [
    "weight gain",
    "hot flashes",
    "sleep issues",
    "mood swings",
    "bone health",
    "heart health",
    "hormone therapy",
    "sexual health",
    "joint pain",
    "skin changes",
    "brain fog",
    "stress",
    "menopause",
  ];

  const desktopOnlySuggestions: Suggestion[] = [
    { text: "manage medications", agent: "generalist" },
    { text: "address low libido", agent: "generalist" },
    { text: "clear brain fog", agent: "generalist" },
  ];

  const desktopAndMobileSuggestions: Suggestion[] = [
    { text: "mental health support", agent: "mental_health_agent" },
    { text: "check symptoms", agent: "generalist" },
    { text: "improve sleep", agent: "generalist" },
    { text: "ease night sweats", agent: "generalist" },
    { text: "lose weight", agent: "generalist" },
    { text: "improve gut health", agent: "generalist" },
  ];

  const suggestions = isMobileWeb()
    ? desktopAndMobileSuggestions
    : [...desktopAndMobileSuggestions, ...desktopOnlySuggestions];

  return { headings, suggestions };
};

// Make sure to update ThemeEnum in chat.py if you change this
export type UseCase =
  | "menopause"
  | "weight-loss"
  | "symptom-checking"
  | "talk-to-a-doctor"
  | "improving-weight"
  | "weight-loss-plans"
  | "medical-treatments-for-weight-loss"
  | "improving-menopause-symptoms"
  | "understanding-menopause-symptoms"
  | "medical-treatments-for-menopause"
  | "uti-relief"
  | "online-uti-treatment"
  | "online-prescription-refill"
  | "free-diagnosis-online";

export interface CarouselItem {
  title: string;
  description?: string;
  image: ImageSourcePropType;
}

export interface UseCaseQuote {
  id: string;
  quote: string;
  name: string;
}

interface Prompts {
  prompt: string;
  agent: Agent;
}

// These are shared among use cases with the same landing destination
interface UseCaseSharedData {
  chips?: string[];
  carouselItems?: CarouselItem[];
  quotes?: UseCaseQuote[];
  secondCarouselV3?: {
    headerTextInPrimaryColor: string;
    headerTextInBrandColor: string;
    items: CarouselItem[];
  };
  atAGlanceV3?: AtAGlanceProps;
  faqV3?: FAQItem[];
  headerV3?: {
    headline: string;
    assistantMessage: TyperControllerProps;
  };
  userTestimonialV3?: TestimonialProps;
  curaiTestimonialV3?: TestimonialProps;
}

export type UseCaseLanding = Partial<Record<UseCase, UseCaseSharedData>>;

// These can each differ by use case
type UseCaseSuggestion = {
  useCase: UseCase;
  pageTitle: string;
  landing?: Landing;
  prompts?: Prompts[];
  firstCarouselV3?: {
    headerTextInPrimaryColor: string;
    headerTextInBrandColor: string;
    items: CarouselItem[];
  };
  autoplayAccordionV3?: {
    drawers: AccordionDrawerProps[];
    headerTextInPrimaryColor: string;
    headerTextInBrandColor: string;
  };
  headerV3?: {
    headline: string;
    assistantMessage: TyperControllerProps;
  };
} & UseCaseSharedData;

export type Landing =
  | "weight-loss"
  | "menopause"
  | "uti-relief"
  | "symptom-checking"
  | "talk-to-a-doctor"
  | "online-prescription-refill"
  | "free-diagnosis-online";

const LANDING_V3_GENERIC_CAROUSEL = {
  headerTextInBrandColor: "and so much more",
  items: [
    {
      title: "Licensed doctors and nurse practitioners available 24/7",
      image: GenericCarousel1,
    },
    {
      title: "Mental health support available on demand",
      image: GenericCarousel2,
    },
    {
      title: "Prescription orders and refills",
      image: GenericCarousel3,
    },
    {
      title: "Lab test ordering and result interpretation",
      image: GenericCarousel4,
    },
  ],
};

const GENERIC_AT_A_GLANCE_V3: AtAGlanceProps = {
  headerTextInPrimaryColor: "Free, expert assistance for your health needs ",
  items: [
    {
      Icon: CheckCircle,
      copy: "Fast relief from symptoms with or without medication",
    },
    {
      Icon: DoctorOutline,
      copy: "Free telehealth visits with board-certified doctors",
    },
    {
      Icon: Clock,
      copy: "Available 24/7",
    },
  ],
};

const PAYMENT_READY_FAQ_ANSWER = `Chatting with ${brandName} is free for everyone. If seeing a clinician makes sense, a subscription for unlimited visits is $39 per month. Otherwise, a single video visit is $49 and a live chat visit is $39. You'll see the exact price before you choose and pay.`;
const RX_RENEWAL_FAQ_ANSWER = `Requesting a prescription renewal online is free. Start by answering a few questions to see whether a renewal may be right for you. You'll still need to pay for any medications, lab tests, or other services you receive.`;
const ASYNC_DIAGNOSIS_FAQ_ANSWER = `Getting started is free. Tell us what's going on, and we'll ask a few questions to see whether an online visit may be right for you. You'll still need to pay for any medications, lab tests, or other services you receive.`;

const GENERIC_FAQ_V3: FAQItem[] = [
  {
    question: `What is ${brandName} and how does it work?`,
    answer: `${brandName} is your personal health assistant — built by a team of physicians, healthcare experts, and technologists who understand how confusing health questions can feel. That's why ${brandName} is grounded in real medical knowledge and science-backed care, so you can get guidance you can trust.\n\nPowered by advanced AI and connected to a licensed telehealth clinic, ${brandName} helps you with everything from daily health questions to personalized coaching and practical tools like checklists or trackers. If you ever need more than a conversation, ${brandName} can seamlessly connect you with one of our clinicians for immediate support, all within the same experience.\n\nHealth questions don't keep business hours — and neither does ${brandName}. Our clinical team is available 24/7, ensuring you always have access to professional help when you need it most.`,
  },
  {
    question: `Who can use ${brandName}?`,
    answer: `${brandName} is available to anyone in the United States who wants reliable, on-demand health support. You must be 18 or older to use ${brandName}. It's currently available only in English.`,
  },
  {
    question: "How much does it cost to use?",
    answer: `${brandName} is completely free — for everyone. And if you want to speak with one of our licensed clinicians, that's free too. No hidden fees, no surprise bills.`,
    paymentAnswer: PAYMENT_READY_FAQ_ANSWER,
  },
  {
    question: `Who created ${brandName}?`,
    answer: `${brandName} is built and operated by Curai Health Medical Group, a leader in AI-powered healthcare.`,
  },
  {
    question: "What if I need a doctor?",
    answer: `${brandName} can seamlessly connect you with one of our licensed clinicians for immediate support, all inside the ${brandName} experience. ${brandName} can also generate a summary for you to bring to your own clinician in person, or whomever you decide to seek care from.\n\nIf you are ever experiencing an emergency, please call 911. If you are having thoughts of suicide or self-harm, please call or text 988.`,
  },
  {
    question: `What does ${brandName} do with my conversations and data?`,
    answer: `Your health information deserves the highest protection. ${brandName} is fully HIPAA and SOC-2 compliant and follows strict medical and legal protocols to safeguard your privacy. Built with the same security standards trusted by hospitals and banks, your data is always encrypted, confidential, and in your control.\n\nThe information you share with ${brandName} is used only to provide accurate and helpful responses. Your personal details are never sold or shared with third parties. If you choose to connect with a clinician, only the care team involved in your case will have limited access to what's necessary to support you.`,
  },
];

const withPaymentFAQAnswer = (
  items: FAQItem[],
  paymentQuestion: string,
  paymentAnswer: FAQItem["answer"],
): FAQItem[] =>
  items.map((item) =>
    item.paymentAnswer ? { ...item, paymentQuestion, paymentAnswer } : item,
  );

export const useCaseSuggestions: UseCaseSuggestion[] = [
  {
    useCase: "menopause",
    pageTitle: "Menopause",
    landing: "menopause",
    prompts: [
      {
        prompt: "Explore HRT options",
        agent: "generalist",
      },
      {
        prompt: "Improve your mood with CBT",
        agent: "generalist",
      },
      {
        prompt: "Get sleep coaching",
        agent: "generalist",
      },
    ],
  },
  {
    useCase: "weight-loss",
    pageTitle: "Weight Loss",
    landing: "weight-loss",
    prompts: [
      {
        prompt: "Explore GLP-1 options",
        agent: "generalist",
      },
      {
        prompt: "Get a diet plan and coaching",
        agent: "generalist",
      },
      {
        prompt: "Get an exercise plan",
        agent: "generalist",
      },
    ],
  },
  {
    useCase: "symptom-checking",
    pageTitle: "Symptom Checking",
    landing: "symptom-checking",
    prompts: [
      {
        prompt: "Check my symptoms",
        agent: "generalist",
      },
      {
        prompt: "Treatment options for my symptoms",
        agent: "generalist",
      },
      {
        prompt: "Talk to a doctor for free",
        agent: "generalist",
      },
    ],
  },
  {
    useCase: "talk-to-a-doctor",
    pageTitle: "Talk to a Doctor",
    landing: "talk-to-a-doctor",
    prompts: [
      {
        prompt: "Check my symptoms",
        agent: "generalist",
      },
      {
        prompt: "Treatment options for my symptoms",
        agent: "generalist",
      },
      {
        prompt: "Talk to a doctor for free",
        agent: "generalist",
      },
    ],
  },
  {
    useCase: "improving-weight",
    pageTitle: "Improving Weight",
    landing: "weight-loss",
    prompts: [
      {
        prompt: "Lose weight by stacking small changes",
        agent: "generalist",
      },
      {
        prompt: "See if I qualify for GLP-1s",
        agent: "generalist",
      },
      {
        prompt: "Talk to a doctor about losing weight",
        agent: "generalist",
      },
    ],
    firstCarouselV3: {
      headerTextInPrimaryColor: "Free expert guidance ",
      headerTextInBrandColor: "for your challenges",
      items: [
        {
          title:
            "I'm doing everything right but the weight just won't come off",
          description: `${shortBrandName} helps you overcome hidden challenges that hold you back.`,
          image: ImproveWeightCarousel1,
        },
        {
          title: "I'm tired of eating the same foods over and over",
          description: `${shortBrandName} creates custom meal plans that match your preferences and schedule.`,
          image: ImproveWeightCarousel2,
        },
        {
          title: "I want to exercise, but I don't like the gym",
          description: `${shortBrandName} builds simple, at-home exercise plans that fit your space, time, and energy.`,
          image: ImproveWeightCarousel3,
        },
        {
          title: "I keep slipping back into old habits",
          description: `${shortBrandName} keeps you accountable with nudges and encouragement tailored to your goals.`,
          image: ImproveWeightCarousel4,
        },
      ],
    },
    autoplayAccordionV3: {
      headerTextInPrimaryColor: "Lose weight ",
      headerTextInBrandColor: "as simple as 1, 2, 3",
      drawers: [
        {
          heading: "Start with one small win",
          body: `${shortBrandName} helps you find a simple, sustainable change that will add up to a big impact.`,
          imageSource: ImproveWeightAccordion1,
        },
        {
          heading: "Turn small wins into habits",
          body: `${shortBrandName} guides you step-by-step to form micro habits that add up to big changes.`,
          imageSource: ImproveWeightAccordion2,
        },
        {
          heading: "Watch the weight come off",
          body: "After a few days of simple changes, you’ll start to notice changes in your body.",
          imageSource: ImproveWeightAccordion3,
        },
      ],
    },
    headerV3: {
      headline: "Free, fast relief from your symptoms",
      assistantMessage: {
        content: [
          `Hey, I'm ${shortBrandName}.`,
          "\n\nIn a few questions, I’ll figure out the best treatment for your symptoms and steps you can take today.",
          "\n\nIf you need medical treatment, I’ll connect you to a board-certified clinician for a free telehealth visit—you’ll only pay for labs and medications.",
          "\n\nHow can I help?",
        ],
      },
    },
  },
  {
    useCase: "weight-loss-plans",
    pageTitle: "Weight Loss Plans",
    landing: "weight-loss",
    prompts: [
      {
        prompt: "Lose weight by stacking small changes",
        agent: "generalist",
      },
      {
        prompt: "Get back in shape",
        agent: "generalist",
      },
      {
        prompt: "See if medication is right for me",
        agent: "generalist",
      },
    ],
    firstCarouselV3: {
      headerTextInPrimaryColor: "Free expert guidance ",
      headerTextInBrandColor: "for your challenges",
      items: [
        {
          title:
            "I'm doing everything right but the weight just won't come off",
          description: `${shortBrandName} helps you overcome hidden challenges that hold you back.`,
          image: ImproveWeightCarousel1,
        },
        {
          title: "I'm tired of eating the same foods over and over",
          description: `${shortBrandName} creates custom meal plans that match your preferences and schedule.`,
          image: ImproveWeightCarousel2,
        },
        {
          title: "I want to exercise, but I don't like the gym",
          description: `${shortBrandName} builds simple, at-home exercise plans that fit your space, time, and energy.`,
          image: ImproveWeightCarousel3,
        },
        {
          title: "I keep slipping back into old habits",
          description: `${shortBrandName} keeps you accountable with nudges and encouragement tailored to your goals.`,
          image: ImproveWeightCarousel4,
        },
      ],
    },
    autoplayAccordionV3: {
      headerTextInPrimaryColor: "Lose weight ",
      headerTextInBrandColor: "as simple as 1, 2, 3",
      drawers: [
        {
          heading: "Start with one small win",
          body: `${shortBrandName} helps you find a simple, sustainable change that will add up to a big impact.`,
          imageSource: ImproveWeightAccordion1,
        },
        {
          heading: "Turn small wins into habits",
          body: `${shortBrandName} guides you step-by-step to form micro habits that add up to big changes.`,
          imageSource: ImproveWeightAccordion2,
        },
        {
          heading: "Watch the weight come off",
          body: "After 3-4 weeks of simple changes, you’ll start to notice changes in your body.",
          imageSource: ImproveWeightAccordion3,
        },
      ],
    },
  },
  {
    useCase: "medical-treatments-for-weight-loss",
    pageTitle: "Medical Treatments for Weight Loss",
    landing: "weight-loss",
    prompts: [
      {
        prompt: "See if I qualify for GLP-1s",
        agent: "generalist",
      },
      {
        prompt: "Which GLP-1 is right for me?",
        agent: "generalist",
      },
      {
        prompt: "Talk to a doctor about GLP-1s",
        agent: "generalist",
      },
    ],
    firstCarouselV3: {
      headerTextInPrimaryColor: "Free expert guidance ",
      headerTextInBrandColor: "for your challenges",
      items: [
        {
          title:
            "I've lost weight before but it comes back. Will GLP-1s be different?",
          description: `${shortBrandName} helps you build habits that keeps weight off for good.`,
          image: MedicalWeightCarousel1,
        },
        {
          title: "I'm worried about side effects",
          description: `${shortBrandName} answers your questions and helps with evidence based strategies to manage side effects.`,
          image: MedicalWeightCarousel2,
        },
        {
          title: "I don't know if I qualify for GLP-1s",
          description: `${shortBrandName} helps you understand typical eligibility criteria and connects you to clinicians that can help.`,
          image: MedicalWeightCarousel3,
        },
        {
          title: "I don't want to be on medication forever",
          description: `${shortBrandName} helps you build habits and routines that keep the weight off long term, without medication`,
          image: MedicalWeightCarousel4,
        },
      ],
    },
    autoplayAccordionV3: {
      headerTextInPrimaryColor: "Lose weight ",
      headerTextInBrandColor: "as simple as 1, 2, 3",
      drawers: [
        {
          heading: "See if GLP-1s are right for you",
          body: `${shortBrandName} will help you find the right treatment and connect you to clinicians for care.`,
          imageSource: MedicalWeightAccordion1,
        },
        {
          heading: "Start your treatment",
          body: "Pick up your medication from your local pharmacy or get it delivered by mail.",
          imageSource: MedicalWeightAccordion2,
        },
        {
          heading: "See and feel results",
          body: "After 3-4 weeks of simple changes, you’ll start to notice changes in your body.",
          imageSource: MedicalWeightAccordion3,
        },
      ],
    },
    headerV3: {
      headline: `${shortBrandName} is your free chat-based weight loss assistant`,
      assistantMessage: {
        content: [
          `Hi, I'm ${shortBrandName}.`,
          "\n\nAfter a short conversation, I’ll determine the best way for you to lose weight.",
          "\n\nIf you need medical treatment or want to talk to a doctor, I’ll connect you to a board-certified clinician for a free telehealth visit—you’ll only pay for labs and medications.",
          "\n\nHow can I help?",
        ],
      },
    },
  },
  {
    useCase: "improving-menopause-symptoms",
    pageTitle: "Improving Menopause Symptoms",
    landing: "menopause",
    prompts: [
      {
        prompt: "Beat hot flashes fast",
        agent: "generalist",
      },
      {
        prompt: "Clear brain fog naturally",
        agent: "generalist",
      },
      {
        prompt: "Finally sleep without sweating",
        agent: "generalist",
      },
    ],
    firstCarouselV3: {
      headerTextInPrimaryColor: "Free expert guidance ",
      headerTextInBrandColor: "for your challenges",
      items: [
        {
          title: "Night sweats and poor sleep are ruining my days",
          description: `${shortBrandName} helps you build symptom management routines that make nights easier.`,
          image: MenopauseCarousel1,
        },
        {
          title: "I'm gaining weight despite eating right",
          description: `${shortBrandName} helps you uncover and overcome the changes in your body that cause weight gain.`,
          image: MenopauseCarousel2,
        },
        {
          title: "My symptoms keep changing",
          description: `${shortBrandName} helps you make sense of your patterns and find ways to thrive in spite of them.`,
          image: MenopauseCarousel3,
        },
        {
          title: "I just don't feel like myself anymore",
          description: `${shortBrandName} guides you through small shifts that help restore your energy, mood, and sense of self.`,
          image: MenopauseCarousel4,
        },
      ],
    },
    autoplayAccordionV3: {
      drawers: [
        {
          heading: "Start with one small win",
          body: `${shortBrandName} helps you find a simple change that makes a real difference in how you feel.`,
          imageSource: ImproveMenopauseAccordion1,
        },
        {
          heading: "Turn small wins into habits",
          body: `${shortBrandName} guides you step-by-step to form micro habits that add up to big changes.`,
          imageSource: ImproveMenopauseAccordion2,
        },
        {
          heading: "Long lasting relief",
          body: "After a couple of weeks of simple changes, you’ll start to notice relief.",
          imageSource: ImproveMenopauseAccordion3,
        },
      ],
      headerTextInPrimaryColor: "Menopause relief ",
      headerTextInBrandColor: "as simple as 1, 2, 3",
    },
  },
  {
    useCase: "understanding-menopause-symptoms",
    pageTitle: "Understanding Menopause Symptoms",
    landing: "menopause",
    prompts: [
      {
        prompt: "Check if it's menopause",
        agent: "generalist",
      },
      {
        prompt: "Why am I having hot flashes?",
        agent: "generalist",
      },
      {
        prompt: "See what's causing my night sweats",
        agent: "generalist",
      },
    ],
    firstCarouselV3: {
      headerTextInPrimaryColor: "Free expert guidance ",
      headerTextInBrandColor: "for your challenges",
      items: [
        {
          title: "Night sweats and poor sleep are ruining my days",
          description: `${shortBrandName} helps you build symptom management routines that make nights easier.`,
          image: MenopauseCarousel1,
        },
        {
          title: "I'm gaining weight despite eating right",
          description: `${shortBrandName} helps you uncover and overcome the changes in your body that cause weight gain.`,
          image: MenopauseCarousel2,
        },
        {
          title: "My symptoms keep changing",
          description: `${shortBrandName} helps you make sense of your patterns and find ways to thrive in spite of them.`,
          image: MenopauseCarousel3,
        },
        {
          title: "I just don't feel like myself anymore",
          description: `${shortBrandName} guides you through small shifts that help restore your energy, mood, and sense of self.`,
          image: MenopauseCarousel4,
        },
      ],
    },
    autoplayAccordionV3: {
      drawers: [
        {
          heading: "Start with one small win",
          body: `${shortBrandName} helps you find a simple change that makes a real difference in how you feel.`,
          imageSource: ImproveMenopauseAccordion1,
        },
        {
          heading: "Turn small wins into habits",
          body: `${shortBrandName} guides you step-by-step to form micro habits that add up to big changes.`,
          imageSource: ImproveMenopauseAccordion2,
        },
        {
          heading: "Long lasting relief",
          body: "After a couple of weeks of simple changes, you’ll start to notice relief.",
          imageSource: ImproveMenopauseAccordion3,
        },
      ],
      headerTextInPrimaryColor: "Menopause relief ",
      headerTextInBrandColor: "as simple as 1, 2, 3",
    },
  },
  {
    useCase: "medical-treatments-for-menopause",
    pageTitle: "Medical Treatments for Menopause",
    landing: "menopause",
    prompts: [
      {
        prompt: "Talk to a doctor about menopause",
        agent: "generalist",
      },
      {
        prompt: "Is HRT right for me?",
        agent: "generalist",
      },
      {
        prompt: "Medical support for hot flashes",
        agent: "generalist",
      },
    ],
    firstCarouselV3: {
      headerTextInPrimaryColor: "Free expert guidance ",
      headerTextInBrandColor: "for your challenges",
      items: [
        {
          title: "Night sweats and poor sleep are ruining my days",
          description: `${shortBrandName} helps you build symptom management routines that make nights easier.`,
          image: MenopauseCarousel1,
        },
        {
          title: "I'm gaining weight despite eating right",
          description: `${shortBrandName} helps you uncover and overcome the changes in your body that cause weight gain.`,
          image: MenopauseCarousel2,
        },
        {
          title: "My symptoms keep changing",
          description: `${shortBrandName} helps you make sense of your patterns and find ways to thrive in spite of them.`,
          image: MenopauseCarousel3,
        },
        {
          title: "I just don't feel like myself anymore",
          description: `${shortBrandName} guides you through small shifts that help restore your energy, mood, and sense of self.`,
          image: MenopauseCarousel4,
        },
      ],
    },
    autoplayAccordionV3: {
      headerTextInPrimaryColor: "Menopause relief ",
      headerTextInBrandColor: "as simple as 1, 2, 3",
      drawers: [
        {
          heading: "See if HRT/MHT is right for you",
          body: `${shortBrandName} will help you find the right treatment and connect you to clinicians if needed.`,
          imageSource: MedicalMenopauseAccordion1,
        },
        {
          heading: "Start your treatment",
          body: "Pick up your medication from your local pharmacy or get it delivered by mail.",
          imageSource: MedicalMenopauseAccordion2,
        },
        {
          heading: "Long lasting relief",
          body: "you’ll start to notice your body responding as your treatment takes effect.",
          imageSource: MedicalMenopauseAccordion3,
        },
      ],
    },
  },
  {
    useCase: "uti-relief",
    pageTitle: "UTI Relief",
    landing: "uti-relief",
    prompts: [
      {
        prompt: "It burns when I pee",
        agent: "generalist",
      },
      {
        prompt: "UTI symptom checker",
        agent: "generalist",
      },
      {
        prompt: "Talk to a doctor for free",
        agent: "generalist",
      },
    ],
  },
  {
    useCase: "online-uti-treatment",
    pageTitle: "Online UTI Treatment",
    landing: "uti-relief",
    prompts: [
      {
        prompt: "It burns when I pee",
        agent: "generalist",
      },
    ],
  },
  {
    useCase: "online-prescription-refill",
    pageTitle: "Online Prescription Refill",
    landing: "online-prescription-refill",
    prompts: [
      {
        prompt: "Refill my prescription",
        agent: "generalist",
      },
    ],
  },
  {
    useCase: "free-diagnosis-online",
    pageTitle: "Free Diagnosis Online",
    landing: "free-diagnosis-online",
    prompts: [
      {
        prompt: "Get a free diagnosis",
        agent: "generalist",
      },
    ],
  },
];

export const useCaseLanding: UseCaseLanding = {
  "weight-loss": {
    carouselItems: [
      {
        title: "Science meets real results",
        description: `${shortBrandName} helps you apply proven methods that actually work.`,
        image: Hormone,
      },
      {
        title: "Lose weight, starting today.",
        description: "Personalized steps from an AI built for your health.",
        image: Salad,
      },
      {
        title: "Smart answers. Real results.",
        description: "Personalized support grounded in clinical know-how.",
        image: Walking,
      },
    ],
    quotes: [
      {
        id: "1",
        quote: `"Using ${shortBrandName} made it finally click for me and I’m actually losing weight now."`,
        name: "- Danielle R.",
      },
      {
        id: "2",
        quote: `“${shortBrandName} helps me stay consistent. I’ve lost 10 pounds and feel way more in control.”`,
        name: "- Chris L.",
      },
      {
        id: "3",
        quote: `“I’ve tried so many apps, but ${shortBrandName} is the only one that feels like it gets me.”`,
        name: "- Priya S.",
      },
    ],
    secondCarouselV3: {
      headerTextInPrimaryColor: `${shortBrandName} is weight loss `,
      ...LANDING_V3_GENERIC_CAROUSEL,
    },
    atAGlanceV3: {
      headerTextInPrimaryColor: "The free weight loss assistant ",
      headerTextInBrandColor: "built just for you",
      items: [
        {
          Icon: WeightScaleOutline,
          copy: "Lose up to 10% of your body weight without medication, or up to 20% if prescribed a GLP-1",
        },
        {
          Icon: DoctorOutline,
          copy: "Built by doctors & behavioral experts",
        },
        {
          Icon: Clock,
          copy: "Available for free, 24/7",
        },
      ],
    },
    userTestimonialV3: {
      headerTextInPrimaryColor: `${shortBrandName} has helped people like you `,
      headerTextInBrandColor: "keep weight off for good",
      image: WeightUserTestimonial,
      quote: `I'd tried every diet. Nothing worked and I blamed myself. ${shortBrandName} helped me keep weight off by understanding what was actually going in my body.`,
      name: "Maria S.",
    },
    curaiTestimonialV3: {
      headerTextInPrimaryColor: "Created by ",
      headerTextInBrandColor: "weight loss, metabolism, and hormone experts",
      image: GenericTestimonial,
      quote: `We created ${shortBrandName} because we know that losing weight is hard and life gets in the way. ${shortBrandName} is purpose-built to support you through the ups and downs.`,
      name: "Dr Davis Liu",
      credential: "Chief Medical Officer",
    },
    faqV3: [
      {
        question: `Is ${brandName} free? What's the catch?`,
        answer: `There's no catch, ${brandName} is completely free for everyone. And if you want to speak with one of our licensed clinicians, that's free too. No hidden fees, no surprise bills.`,
        paymentAnswer: PAYMENT_READY_FAQ_ANSWER,
      },
      {
        question: `How does ${brandName} help people lose weight?`,
        answer: `${brandName} helps people lose weight by making simple lifestyle changes that add up to real results. ${brandName} is an AI assistant that builds personalized, evidence-based weight loss plans tailored to your preferences and circumstances. ${brandName} can also connect users to licensed clinicians at Curai Health Medical Group for weight loss medications, if they qualify.`,
      },
      {
        question: `What weight-loss medication does ${brandName} offer?`,
        answer: `${brandName} does not prescribe medications. When appropriate, ${brandName} can connect you to the Curai Health Medical Group clinic, where a licensed clinician may evaluate whether FDA-approved prescription medications for weight management are appropriate. Clinicians may prescribe FDA-approved GLP-1 medicines for weight loss, which include semaglutide (Wegovy) as a once-daily oral tablet or a once-weekly injectable pen, tirzepatide (Zepbound) as a once-weekly injection, and liraglutide (Saxenda) as a once-daily injection. Our clinic does not prescribe controlled substances, compounded medicines, or medicines that are off-label use for weight loss.\n\nAt this time due to state regulations, we are not able to prescribe these medicines in Alabama, Connecticut, Florida, Louisiana, or Mississippi. In California, Indiana, New Jersey, and New York, these medicines require a live visit with a clinician.`,
      },
      {
        question: "How long does it take to see progress?",
        answer:
          "Most people start to notice changes within 3–4 weeks. With clinical treatments, such as prescription weight-loss medications, appetite and weight changes may begin sooner, but meaningful, sustained results still build over several months.",
      },
      {
        question: `Is ${brandName} a replacement for professional help?`,
        answer: `No. ${brandName} doesn't replace human care or professional help. It's an AI-powered tool that helps you better understand your health, stay organized, and know when it's time to see a doctor. When that time comes, ${brandName} can connect you directly with a licensed clinician or help you prepare for your own visit.\n\nIf you are ever experiencing an emergency, please call 911. If you are having thoughts of suicide or self-harm, please call or text 988.`,
      },
      {
        question: "What if I need a doctor?",
        answer: `${brandName} can seamlessly connect you with one of our licensed clinicians for immediate support, all inside the ${brandName} experience. ${brandName} can also generate a summary for you to bring to your own clinician in person, or whomever you decide to seek care from.\n\nIf you are ever experiencing an emergency, please call 911. If you are having thoughts of suicide or self-harm, please call or text 988.`,
      },
    ],
    headerV3: {
      headline: `${shortBrandName} is your free chat-based weight loss assistant`,
      assistantMessage: {
        content: [
          `Hey there 👋 I'm ${shortBrandName}, your free health assistant. I've already helped over 11,000 people like you lose weight and hit other health goals.`,
          "\n\nHow can I help you today?",
        ],
        boldStartIdx: 51,
        boldEndIdx: 111,
      },
    },
  },
  menopause: {
    carouselItems: [
      {
        title: "Explore HRT options",
        description:
          "Learn if hormone therapy could be right for your symptoms.",
        image: Pills,
      },
      {
        title: "Improve your mood with CBT",
        description:
          "Practice quick exercises that ease irritability and stress.",
        image: Headphones,
      },
      {
        title: "Get sleep coaching",
        description:
          "Learn science-backed tips to beat menopause sleep struggles.",
        image: WomanSleeping,
      },
    ],
    quotes: [
      {
        id: "1",
        quote:
          '"I finally feel like myself again. Getting clarity on my menopause changed everything."',
        name: "- Jennifer R.",
      },
      {
        id: "2",
        quote: `“${shortBrandName} helped me get HRT, and the difference has been life-changing. My hot flashes feel manageable.”`,
        name: "- Maria S.",
      },
      {
        id: "3",
        quote: `“For the first time my symptoms actually made sense, all thanks to ${shortBrandName}.”`,
        name: "- Patricia L.",
      },
    ],
    secondCarouselV3: {
      headerTextInPrimaryColor: `${shortBrandName} is menopause `,
      ...LANDING_V3_GENERIC_CAROUSEL,
    },
    atAGlanceV3: {
      headerTextInPrimaryColor: "The free menopause support assistant ",
      headerTextInBrandColor: "built just for you",
      items: [
        {
          Icon: CheckCircle,
          copy: "Fast relief from symptoms with or without medication",
        },
        {
          Icon: DoctorOutline,
          copy: "Built by doctors & behavioral experts",
        },
        {
          Icon: Clock,
          copy: "Available for free, 24/7",
        },
      ],
    },
    faqV3: [
      {
        question: `Is ${brandName} free? What's the catch?`,
        answer: `There's no catch, ${brandName} is completely free for everyone. And if you want to speak with one of our licensed clinicians, that's free too. No hidden fees, no surprise bills.`,
        paymentAnswer: PAYMENT_READY_FAQ_ANSWER,
      },
      {
        question: `How does ${brandName} help people with menopause?`,
        answer: `${brandName} helps people manage menopause symptoms by making simple lifestyle changes that add up to real results. ${brandName} is an AI assistant that builds personalized, evidence-based menopause support plans tailored to your preferences and circumstances. ${brandName} can also connect users to licensed clinicians at Curai Health Medical Group for medical care options, if they qualify.`,
      },
      {
        question: `What menopause medications does ${brandName} offer?`,
        answer: `${brandName} does not prescribe medications. When appropriate, ${brandName} can connect you to Curai Health Medical Group, where a licensed clinician may offer evidence-based treatments of topical estrogen patches for hot flashes, and progesterone pills for women who have a uterus are available options. Vaginal estrogen cream is also available for women with symptoms of irritation of the vaginal area due to genitourinary syndrome of menopause (GSM). Compounded estrogen or progesterone treatments are not available.`,
      },
      {
        question: "How long does it take to see progress?",
        answer:
          "Many people notice early improvements within a few weeks, depending on the symptoms and approach. With clinical treatments, including hormone therapy or other prescription options, some symptoms like hot flashes or night sweats may improve more quickly, often within weeks.",
      },
      {
        question: `Is ${brandName} a replacement for professional help?`,
        answer: `No. ${brandName} doesn't replace human care or professional help. It's an AI-powered tool that helps you better understand your health, stay organized, and know when it's time to see a doctor. When that time comes, ${brandName} can connect you directly with a licensed clinician or help you prepare for your own visit.\n\nIf you are ever experiencing an emergency, please call 911. If you are having thoughts of suicide or self-harm, please call or text 988.`,
      },
      {
        question: "What if I need a doctor?",
        answer: `${brandName} can seamlessly connect you with one of our licensed clinicians for immediate support, all inside the ${brandName} experience. ${brandName} can also generate a summary for you to bring to your own clinician in person, or whomever you decide to seek care from.\n\nIf you are ever experiencing an emergency, please call 911. If you are having thoughts of suicide or self-harm, please call or text 988.`,
      },
    ],
    headerV3: {
      headline: `${shortBrandName} is your free chat-based menopause support assistant`,
      assistantMessage: {
        content: [
          `Hey there 👋 I'm ${shortBrandName}, your free health assistant. I've already helped over 11,000 people like you manage symptoms and hit other health goals.`,
          "\n\nHow can I help you today?",
        ],
        boldStartIdx: 51,
        boldEndIdx: 116,
      },
    },
    userTestimonialV3: {
      headerTextInPrimaryColor: `${shortBrandName} has helped people like you `,
      headerTextInBrandColor: "feel relief from menopause",
      image: MenopauseUserTestimonial,
      quote: `I finally feel like I understand what's happening. ${shortBrandName} helped me make small changes that added up to real relief and made menopause manageable.`,
      name: "Maria S.",
    },
    curaiTestimonialV3: {
      headerTextInPrimaryColor: "Created by ",
      headerTextInBrandColor:
        "experts in menopause, hormone, and women's health",
      image: GenericTestimonial,
      quote: `We created ${shortBrandName} because menopause is confusing, inconsistent, and often dismissed. ${shortBrandName} helps people take control of their situation.`,
      name: "Dr Davis Liu",
      credential: "Chief Medical Officer",
    },
  },
  "uti-relief": {
    secondCarouselV3: {
      headerTextInPrimaryColor: `${shortBrandName} offers UTI relief `,
      ...LANDING_V3_GENERIC_CAROUSEL,
    },
    atAGlanceV3: GENERIC_AT_A_GLANCE_V3,
    faqV3: GENERIC_FAQ_V3,
    headerV3: {
      headline: "Free, fast relief from your UTI symptoms",
      assistantMessage: {
        content: [
          `Hi, I'm ${shortBrandName}.`,
          "\n\nIn a few questions, I’ll determine treatment options for your UTI symptoms.",
          "\n\nIf you need medical treatment or want to talk to a doctor, I’ll connect you to a board-certified clinician for a free telehealth visit—you’ll only pay for labs and medications.",
          "\n\nHow can I help?",
        ],
      },
    },
  },
  "symptom-checking": {
    secondCarouselV3: {
      headerTextInPrimaryColor: `${shortBrandName} offers symptom relief `,
      ...LANDING_V3_GENERIC_CAROUSEL,
    },
    atAGlanceV3: GENERIC_AT_A_GLANCE_V3,
    faqV3: GENERIC_FAQ_V3,
    headerV3: {
      headline: "Free, fast relief from your symptoms",
      assistantMessage: {
        content: [
          `Hey, I'm ${shortBrandName}.`,
          "\n\nIn a few questions, I’ll figure out the best treatment for your symptoms.",
          "\n\nIf you need medical treatment, I’ll connect you to a board-certified clinician for a free telehealth visit—you’ll only pay for labs and medications.",
          "\n\nHow can I help?",
        ],
      },
    },
  },
  "online-prescription-refill": {
    atAGlanceV3: {
      headerTextInPrimaryColor: "Safe, secure, and clinician reviewed",
      items: [
        {
          Icon: DoctorOutline,
          copy: "All refills are reviewed by U.S. based licensed clinicians",
        },
        {
          Icon: ClipboardList,
          copy: "HIPAA compliant",
        },
        {
          Icon: Lock,
          copy: "Secure and private",
        },
      ],
    },
    faqV3: [
      {
        question: "What prescriptions can Curai refill?",
        answer:
          "Curai can help refill many common maintenance medications, including those for:\n\n• Blood pressure\n• Cholesterol\n• Asthma and inhalers\n• Birth control\n• Diabetes\n• Thyroid conditions\n• Allergies\n• Many other everyday prescriptions\n\nA licensed clinician reviews every request before sending a refill to your pharmacy.",
      },
      {
        question: "What prescriptions can't be refilled?",
        answer:
          "For safety and regulatory reasons, Curai does not prescribe controlled substances. This includes:\n\n• Opioid pain medications (e.g., oxycodone, hydrocodone)\n• Benzodiazepines (e.g., Xanax, Ativan)\n• ADHD stimulant medications (e.g., Adderall, Ritalin)\n\nWe also may not refill medications that require close monitoring, specialist care, or in-person evaluation.\n\nIf your medication isn’t eligible to be refilled, we’ll let you know and help guide you to the right next step.",
      },
      ...withPaymentFAQAnswer(
        GENERIC_FAQ_V3,
        "How much does a prescription renewal cost?",
        RX_RENEWAL_FAQ_ANSWER,
      ),
    ],
  },
  "free-diagnosis-online": {
    atAGlanceV3: {
      headerTextInPrimaryColor: "Safe, secure, and clinician reviewed",
      items: [
        {
          Icon: DoctorOutline,
          copy: "All cases are reviewed by U.S. based licensed clinicians",
        },
        {
          Icon: ClipboardList,
          copy: "HIPAA compliant",
        },
        {
          Icon: Lock,
          copy: "Secure and private",
        },
      ],
    },
    faqV3: [
      {
        question: "What conditions can Curai help with?",
        answer:
          "Curai may be able to help with common, non-emergency health concerns, including:\n\n• UTIs and common vaginal infections\n• Cold sores\n• Birth control\n• Erectile dysfunction\n• High cholesterol\n• Stable thyroid conditions\n• Prediabetes and stable type 2 diabetes\n• High blood pressure\n• Colds and sore throats\n• Sinus symptoms\n• Cough and bronchitis\n• Mild nausea, vomiting, or diarrhea\n• Back pain\n• Mild-to-moderate anxiety or depression\n\nWhether online diagnosis is right for you depends on your symptoms, health history, and other safety factors. If it isn't the right fit, we'll help you figure out the next step.",
      },
      {
        question: "What can't Curai help with?",
        answer:
          "Curai cannot assist with emergency situations, severe symptoms, or conditions that require a physical (in-person) examination.",
      },
      ...withPaymentFAQAnswer(
        GENERIC_FAQ_V3,
        "How much does an online diagnosis cost?",
        ASYNC_DIAGNOSIS_FAQ_ANSWER,
      ),
    ],
  },
  "talk-to-a-doctor": {
    secondCarouselV3: {
      headerTextInPrimaryColor: `${shortBrandName} offers symptom relief `,
      ...LANDING_V3_GENERIC_CAROUSEL,
    },
    atAGlanceV3: GENERIC_AT_A_GLANCE_V3,
    faqV3: GENERIC_FAQ_V3,
    headerV3: {
      headline: `${shortBrandName} is a health AI that can connect you to a doctor for a free visit`,
      assistantMessage: {
        content: [
          "In a few questions, I’ll determine the best options for your situation.",
          "\n\nIf you need medical treatment or want to talk to a doctor, I’ll connect you to a board-certified clinician for a free telehealth visit—you’ll only pay for labs and medications.",
          "\n\nHow can I help?",
        ],
      },
    },
  },
};

export const useUseCaseSuggestions = (useCase: UseCase): UseCaseSuggestion => {
  const suggestions = useCaseSuggestions.find((uc) => uc.useCase === useCase);

  if (!suggestions) {
    return {
      useCase,
      pageTitle: useCase,
    };
  }

  if (suggestions.landing) {
    const landingData = useCaseLanding[suggestions.landing as UseCase];
    return {
      ...suggestions,
      ...landingData,
      // Use-case-specific headerV3 overrides landing's headerV3
      headerV3: suggestions.headerV3 ?? landingData?.headerV3,
    };
  }

  return suggestions;
};
